import {
  PaperclipIcon,
  CaretDownIcon,
  CaretUpIcon,
  TranslateIcon,
} from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useTranslateInboundEmail } from 'src/api/communications/inbound-emails/useTranslateInboundEmail';
import { useEmailThreadViewer } from './hooks/useEmailThreadViewer';
import type { EmailThreadEmail, TranslatedEmailContent } from '@erp/rpc';
import {
  renderEmailAttachments,
  renderEmailBody,
  buildCidMap,
} from './email-thread-utils';
import { getLanguageDisplayName } from 'src/lib/language-utils';

function getTranslation(
  translatedTextContent:
    | Record<string, string | TranslatedEmailContent>
    | null
    | undefined,
  lang: string,
): TranslatedEmailContent | null {
  if (!translatedTextContent) return null;
  const translation = translatedTextContent[lang];
  if (!translation) return null;

  if (typeof translation === 'string') {
    return { body: translation, isHtml: false };
  }

  return translation;
}

export interface EmailThreadViewerProps {
  conversationId: string | null;
  fetchOCR?: boolean;
  fetchExtractedPurchaseOrder?: boolean;
  onEmailSelect?: (email: EmailThreadEmail) => void;
  /** Document IDs to highlight (for showing source documents of invoices/POs) */
  highlightedDocumentIds?: string[];
  /** Email IDs to highlight */
  highlightedEmailIds?: string[];
}

export function EmailThreadViewer({
  conversationId,
  fetchOCR: _fetchOCR,
  fetchExtractedPurchaseOrder: _fetchExtractedPurchaseOrder,
  onEmailSelect,
  highlightedDocumentIds = [],
  highlightedEmailIds = [],
}: EmailThreadViewerProps) {
  const {
    threadData,
    isLoading,
    error,
    threadMetadata,
    expandedEmails,
    toggleEmailExpanded,
    expandAllEmails,
    collapseAllEmails,
  } = useEmailThreadViewer({
    conversationId,
    onEmailSelect,
  });

  const [showOriginalByEmailId, setShowOriginalByEmailId] = useState<
    Record<string, boolean>
  >({});
  const [lastTranslateAttemptEmailId, setLastTranslateAttemptEmailId] =
    useState<string | null>(null);

  const emailRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const translateMutation = useTranslateInboundEmail(conversationId);

  useEffect(() => {
    if (highlightedEmailIds.length > 0) {
      const firstHighlightedId = highlightedEmailIds[0];
      const element = emailRefs.current.get(firstHighlightedId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [highlightedEmailIds]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16 h-48">
        <p className="text-sm text-muted-foreground">Loading email thread...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-16 h-48">
        <p className="text-sm text-destructive">
          Error loading email thread: {error.message}
        </p>
      </div>
    );
  }

  if (!threadData?.emails?.length) {
    return (
      <div className="flex items-center justify-center p-16 h-48">
        <p className="text-sm text-muted-foreground">
          No emails found in this thread
        </p>
      </div>
    );
  }

  const renderThreadHeader = () => (
    <div className="space-y-2">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            Participants
          </span>
        </div>
        <p className="text-sm">
          {threadMetadata?.participantEmails.join(', ')}
        </p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={expandAllEmails}>
          Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAllEmails}>
          Collapse All
        </Button>
      </div>
    </div>
  );

  const renderThreadEmails = () => (
    <div className="space-y-1">
      {threadData.emails.map((email, index) => {
        const isExpanded = expandedEmails.has(email.id);
        // const isLatest = index === threadData.emails.length - 1;
        const isHighlighted = highlightedEmailIds.includes(email.id);
        const normalizedDetectedLanguage = email.detectedLanguage
          ?.trim()
          .toLowerCase();
        const isOutboundEmail = email.service === 'microsoft-graph-outbound';
        const translation = getTranslation(email.translatedTextContent, 'en');
        const hasEnglishTranslation = Boolean(
          translation?.body || translation?.subject,
        );
        const canTranslateToEnglish =
          !isOutboundEmail &&
          normalizedDetectedLanguage &&
          normalizedDetectedLanguage !== 'en' &&
          !hasEnglishTranslation;
        const showOriginal = showOriginalByEmailId[email.id];

        return (
          <div
            key={email.id}
            className="group"
            ref={(el) => {
              if (el) {
                emailRefs.current.set(email.id, el);
              } else {
                emailRefs.current.delete(email.id);
              }
            }}
          >
            {/* Email item */}
            <div className="relative">
              {/* Email content */}
              <div className="pb-2">
                {/* Collapsed view */}
                {!isExpanded ? (
                  <div
                    className={`bg-muted/30 rounded-md p-3 border ${
                      isHighlighted
                        ? 'border-primary border-2 bg-primary/5'
                        : 'border-border/50'
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium text-sm truncate flex items-center gap-1">
                        {hasEnglishTranslation && translation?.subject ? (
                          <>
                            {translation.subject}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <TranslateIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Translated from{' '}
                                {getLanguageDisplayName(
                                  normalizedDetectedLanguage,
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </>
                        ) : (
                          email.subject || 'No Subject'
                        )}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground">
                          {new Date(email.createdAt).toLocaleDateString([], {
                            month: 'short',
                            day: 'numeric',
                          })}{' '}
                          {new Date(email.createdAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleEmailExpanded(email.id);
                          }}
                        >
                          <CaretDownIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span className="truncate">{email.from}</span>
                      {(email.attachmentCount ?? 0) > 0 && (
                        <>
                          <span>•</span>
                          <div className="flex items-center gap-1">
                            <PaperclipIcon size={10} />
                            <span>{email.attachmentCount}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Expanded view */
                  <div
                    className={`bg-muted/30 rounded-md p-3 border ${
                      isHighlighted
                        ? 'border-primary border-2 bg-primary/5'
                        : 'border-border/50'
                    }`}
                  >
                    <div className="space-y-2">
                      {/* Header */}
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-semibold text-sm flex-1 flex items-center gap-1">
                            {hasEnglishTranslation &&
                            !showOriginal &&
                            translation?.subject ? (
                              <>
                                {translation.subject}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <TranslateIcon className="h-3 w-3 text-muted-foreground shrink-0" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Translated from{' '}
                                    {getLanguageDisplayName(
                                      normalizedDetectedLanguage,
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            ) : (
                              email.subject || 'No Subject'
                            )}
                          </h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEmailExpanded(email.id);
                            }}
                          >
                            <CaretUpIcon className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            <div>
                              <span className="font-medium">From:</span>{' '}
                              {email.from}
                            </div>
                            <div>
                              <span className="font-medium">To:</span>{' '}
                              {email.to}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {new Date(email.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="pt-2 border-t">
                        {!isOutboundEmail &&
                        normalizedDetectedLanguage &&
                        normalizedDetectedLanguage !== 'en' ? (
                          <div className="flex flex-col gap-1 mb-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs text-muted-foreground">
                                {normalizedDetectedLanguage
                                  ? hasEnglishTranslation
                                    ? `Translated from ${getLanguageDisplayName(normalizedDetectedLanguage)}`
                                    : null
                                  : hasEnglishTranslation
                                    ? 'Translated'
                                    : null}
                              </div>

                              {!hasEnglishTranslation ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={translateMutation.isPending}
                                  onClick={() => {
                                    setLastTranslateAttemptEmailId(email.id);
                                    translateMutation.mutate({
                                      inboundEmailId: email.id,
                                      targetLanguage: 'en',
                                    });
                                  }}
                                >
                                  <TranslateIcon className="h-3 w-3 mr-1" />
                                  {translateMutation.isPending &&
                                  lastTranslateAttemptEmailId === email.id
                                    ? 'Translating...'
                                    : 'Translate to English'}
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setShowOriginalByEmailId((prev) => ({
                                      ...prev,
                                      [email.id]: !prev[email.id],
                                    }));
                                  }}
                                >
                                  {showOriginal
                                    ? 'See translation'
                                    : 'See original'}
                                </Button>
                              )}
                            </div>

                            {translateMutation.error &&
                            lastTranslateAttemptEmailId === email.id ? (
                              <div className="text-xs text-destructive">
                                Translation failed. Please try again.
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {hasEnglishTranslation &&
                        !showOriginal &&
                        translation?.body ? (
                          <div className="prose prose-sm max-w-none">
                            {translation.isHtml ? (
                              renderEmailBody(
                                translation.body,
                                buildCidMap(email.documentAssociations),
                              )
                            ) : (
                              <pre className="whitespace-pre-wrap text-sm leading-relaxed">
                                {translation.body}
                              </pre>
                            )}
                          </div>
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            {renderEmailBody(
                              email.messageBodyHtml ||
                                email.htmlContent ||
                                email.textContent,
                              buildCidMap(email.documentAssociations),
                            )}
                          </div>
                        )}
                      </div>

                      {/* Attachments */}
                      {((email.attachments?.length ?? 0) > 0 ||
                        (email.documentAssociations?.length ?? 0) > 0) && (
                        <div className="pt-2 border-t">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
                            <PaperclipIcon size={12} />
                            <span>
                              {(email.attachments?.length ?? 0) +
                                (email.documentAssociations?.length ?? 0)}{' '}
                              attachment
                              {(email.attachments?.length ?? 0) +
                                (email.documentAssociations?.length ?? 0) !==
                              1
                                ? 's'
                                : ''}
                            </span>
                          </div>
                          {renderEmailAttachments(
                            email.attachments,
                            email.documentAssociations,
                            highlightedDocumentIds,
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0 overflow-auto">
      <div className="space-y-2">
        {renderThreadHeader()}
        {renderThreadEmails()}
      </div>
    </div>
  );
}
